import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useNav } from './hooks/useNav';
import { Header } from './components/Header';
import { HomeView } from './views/HomeView';
import { DetailView } from './views/DetailView';
import { LetrasView } from './views/LetrasView';
import { ViewTransition } from './components/ViewTransition';
import { useEdgeGestures } from './hooks/useEdgeGestures';
import { useViewportHeightFix } from './hooks/useViewportHeightFix';
import { DataProvider, useData } from './store/data';
import { GAMES } from './games/registry';

import type { View } from './types';

// Lazy-loaded chunks. Home, Detail, Letras + Header stay eager (frecuente).
// Resto se carga on-demand → bundle inicial más liviano. PWA precachea los
// chunks vía globPatterns en vite.config.ts → offline-first preservado.
const SilabasView      = lazy(() => import('./views/SilabasView').then((m) => ({ default: m.SilabasView })));
const CuentosView      = lazy(() => import('./views/CuentosView').then((m) => ({ default: m.CuentosView })));
const CuentoReaderView = lazy(() => import('./views/CuentoReaderView').then((m) => ({ default: m.CuentoReaderView })));
const BuscarView       = lazy(() => import('./views/BuscarView').then((m) => ({ default: m.BuscarView })));
const BuscarSceneView  = lazy(() => import('./views/BuscarSceneView').then((m) => ({ default: m.BuscarSceneView })));

const HistoriasJWView      = lazy(() => import('./views/HistoriasJWView').then((m) => ({ default: m.HistoriasJWView })));
const HistoriaJWReaderView = lazy(() => import('./views/HistoriaJWReaderView').then((m) => ({ default: m.HistoriaJWReaderView })));
const ComicsJWView         = lazy(() => import('./views/ComicsJWView').then((m) => ({ default: m.ComicsJWView })));
const ComicJWReaderView    = lazy(() => import('./views/ComicJWReaderView').then((m) => ({ default: m.ComicJWReaderView })));
const VideosJWView         = lazy(() => import('./views/VideosJWView').then((m) => ({ default: m.VideosJWView })));
const VideoJWPlayerView    = lazy(() => import('./views/VideoJWPlayerView').then((m) => ({ default: m.VideoJWPlayerView })));

const AdminLayout  = lazy(() => import('./views/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })));
const AdminEstudio = lazy(() => import('./views/admin/estudio/AdminEstudio').then((m) => ({ default: m.AdminEstudio })));
const AdminWords   = lazy(() => import('./views/admin/AdminWords').then((m) => ({ default: m.AdminWords })));
const AdminBuscar  = lazy(() => import('./views/admin/AdminBuscar').then((m) => ({ default: m.AdminBuscar })));
const AdminCuentos = lazy(() => import('./views/admin/AdminCuentos').then((m) => ({ default: m.AdminCuentos })));
const AdminOraciones = lazy(() => import('./views/admin/AdminOraciones').then((m) => ({ default: m.AdminOraciones })));
const AdminMascotas = lazy(() => import('./views/admin/mascotas/AdminMascotas').then((m) => ({ default: m.AdminMascotas })));
const AdminPiezas = lazy(() => import('./views/admin/piezas/AdminPiezas').then((m) => ({ default: m.AdminPiezas })));
const AdminPersonajes = lazy(() => import('./views/admin/AdminPersonajes').then((m) => ({ default: m.AdminPersonajes })));
const AdminLugares    = lazy(() => import('./views/admin/AdminLugares').then((m) => ({ default: m.AdminLugares })));
const AdminHistoriasJW = lazy(() => import('./views/admin/AdminHistoriasJW').then((m) => ({ default: m.AdminHistoriasJW })));
const AdminComicsJW    = lazy(() => import('./views/admin/AdminComicsJW').then((m) => ({ default: m.AdminComicsJW })));
const AdminVideosJW    = lazy(() => import('./views/admin/AdminVideosJW').then((m) => ({ default: m.AdminVideosJW })));
const AdminLetras  = lazy(() => import('./views/admin/AdminLetras').then((m) => ({ default: m.AdminLetras })));
const AdminLibroA4 = lazy(() => import('./views/admin/AdminLibroA4').then((m) => ({ default: m.AdminLibroA4 })));
const AdminCuentosA4 = lazy(() => import('./views/admin/AdminCuentosA4').then((m) => ({ default: m.AdminCuentosA4 })));
const AdminEstilos = lazy(() => import('./views/admin/estilos/AdminEstilos').then((m) => ({ default: m.AdminEstilos })));
const AdminEscenasMentales = lazy(() => import('./views/admin/AdminEscenasMentales').then((m) => ({ default: m.AdminEscenasMentales })));
const AdminConfig  = lazy(() => import('./views/admin/AdminConfig').then((m) => ({ default: m.AdminConfig })));

function LazyFallback() {
  return (
    <div className="empty-state">
      <div className="loading-dots" aria-label="Cargando"><span /><span /><span /></div>
    </div>
  );
}

const TITLES: Partial<Record<View['name'], string>> = {
  home: 'Clarisa Hub',
  detail: 'Tarjetas',
  letras: 'Letras',
  silabas: 'Sílabas',
  'buscar-list': 'Buscar',
  'buscar-scene': 'Buscar',
  'cuentos-list': 'Cuentos',
  'cuento-reader': 'Cuento',
  'jw-historias-list': 'Historias JW',
  'jw-historia-reader': 'Historia',
  'jw-comics-list': 'Cómics JW',
  'jw-comic-reader': 'Cómic',
  'jw-videos-list': 'Videos JW',
  'jw-video-player': 'Video',
  admin: 'Admin'
};

function getTitle(view: View): string {
  if (view.name === 'game') return GAMES[view.game].title;
  if (view.name === 'detail') {
    if (view.category === 'TODAS') return 'Todas';
    if (view.category === 'NUMERO') return 'Números';
    if (view.category === 'COLOR') return 'Colores';
    return view.category.charAt(0) + view.category.slice(1).toLowerCase();
  }
  return TITLES[view.name] ?? 'Clarisa Hub';
}

function AppContent() {
  const viewWrapperRef = useRef<HTMLDivElement>(null);
  // Reverse of Tile.tsx's forward tagging: if the view we're leaving was
  // opened via a morphed tile tap, tag this wrapper as the transition's
  // "old" element (synchronously, right before useNav starts the browser's
  // startViewTransition capture) so it shrinks back into the origin tile
  // instead of a plain cross-fade. useNav calls this for every back that
  // leaves a morphed view — the app's own and the browser's. See
  // ViewTransition.tsx/HomeView.tsx for the "new" side of this same transition.
  const tagMorphBack = useCallback(() => {
    if (viewWrapperRef.current) viewWrapperRef.current.style.viewTransitionName = 'tile-morph';
  }, []);
  const nav = useNav({ name: 'home' }, tagMorphBack);
  const { scenes, cuentos } = useData();
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);

    // Solicitar almacenamiento persistente nativo
    if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.persist) {
      navigator.storage.persist().then((persisted) => {
        console.log('PWA Storage Persisted:', persisted);
      }).catch(() => {});
    }

    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // Sincronizar App Badge con novedades (total de cuentos + escenas de búsqueda)
  useEffect(() => {
    if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
      const totalNovedades = (scenes?.length ?? 0) + (cuentos?.length ?? 0);
      if (totalNovedades > 0) {
        (navigator as any).setAppBadge(totalNovedades).catch(() => {});
      } else {
        (navigator as any).clearAppBadge().catch(() => {});
      }
    }
  }, [scenes, cuentos]);

  const showBack = nav.view.name !== 'home';
  useEdgeGestures(showBack ? nav.pop : null, nav.view.name !== 'home' ? nav.home : null);
  useViewportHeightFix();

  // Pause home tile animations when navigating away — frees GPU/CPU
  useEffect(() => {
    if (nav.view.name !== 'home') {
      document.body.classList.add('nav-away');
    } else {
      document.body.classList.remove('nav-away');
    }
    return () => document.body.classList.remove('nav-away');
  }, [nav.view.name]);

  const getTitleWithScene = (view: View) => {
    if (view.name === 'buscar-scene') {
      const scene = scenes[view.idx];
      return scene ? `${scene.title || 'Buscar'} (${view.idx + 1} de ${scenes.length})` : 'Buscar';
    }
    return getTitle(view);
  };

  const GameView = nav.view.name === 'game' ? GAMES[nav.view.game].component : null;

  return (
    <div className="app">
      <div className="ambient" aria-hidden="true" />
      {nav.view.name !== 'home' && (
        <Header
          title={getTitleWithScene(nav.view)}
          showBack={showBack}
          onBack={nav.pop}
          onHome={nav.home}
          online={online}
        />
      )}
      <main className="app-main">
        <ViewTransition viewKey={JSON.stringify(nav.view)} dir={nav.dir} morph={nav.morph} wrapperRef={viewWrapperRef}>
        <Suspense fallback={<LazyFallback />}>
        {nav.view.name === 'home' && <HomeView onOpen={nav.push} poppedFrom={nav.poppedFrom} />}
        {nav.view.name === 'detail' && <DetailView category={nav.view.category} />}
        {nav.view.name === 'letras' && <LetrasView />}
        {nav.view.name === 'silabas' && <SilabasView />}
        {nav.view.name === 'buscar-list' && <BuscarView onOpen={nav.push} />}
        {nav.view.name === 'buscar-scene' && <BuscarSceneView idx={nav.view.idx} onOpen={nav.push} />}
        {nav.view.name === 'cuentos-list' && <CuentosView onOpen={nav.push} />}
        {nav.view.name === 'cuento-reader' && <CuentoReaderView idx={nav.view.idx} />}
        {nav.view.name === 'jw-historias-list' && <HistoriasJWView onOpen={nav.push} />}
        {nav.view.name === 'jw-historia-reader' && <HistoriaJWReaderView idx={nav.view.idx} />}
        {nav.view.name === 'jw-comics-list' && <ComicsJWView onOpen={nav.push} />}
        {nav.view.name === 'jw-comic-reader' && <ComicJWReaderView idx={nav.view.idx} />}
        {nav.view.name === 'jw-videos-list' && <VideosJWView onOpen={nav.push} />}
        {nav.view.name === 'jw-video-player' && <VideoJWPlayerView idx={nav.view.idx} />}
        {GameView && <GameView />}
        {nav.view.name === 'admin' && (
          <AdminLayout>
            {(tab) => (
              <Suspense fallback={<LazyFallback />}>
                {tab === 'grabaciones' && <AdminEstudio />}
                {tab === 'palabras' && <AdminWords />}
                {tab === 'buscar'   && <AdminBuscar />}
                {tab === 'cuentos'     && <AdminCuentos />}
                {tab === 'oraciones'   && <AdminOraciones />}
                {tab === 'escenas-mentales' && <AdminEscenasMentales />}
                {tab === 'personajes'  && <AdminPersonajes />}
                {tab === 'mascotas'    && <AdminMascotas />}
                {tab === 'piezas'      && <AdminPiezas />}
                {tab === 'lugares'     && <AdminLugares />}
                {tab === 'estilos'     && <AdminEstilos />}
                {tab === 'historias-jw' && <AdminHistoriasJW />}
                {tab === 'comics-jw'    && <AdminComicsJW />}
                {tab === 'videos-jw'    && <AdminVideosJW />}
                {tab === 'letras'   && <AdminLetras />}
                {tab === 'libro'    && <AdminLibroA4 />}
                {tab === 'cuentos-pdf' && <AdminCuentosA4 />}
                {tab === 'config'   && <AdminConfig />}
              </Suspense>
            )}
          </AdminLayout>
        )}
        </Suspense>
        </ViewTransition>
      </main>
    </div>
  );
}

export function App() {
  return (
    <DataProvider>
      <AppContent />
    </DataProvider>
  );
}
